import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useArticles } from '../hooks/articles';
import { siteConfig } from '../siteData';
import Header from '../components/Header';
import ArticleActions from '../components/ArticleActions';
import { formatArticleDate } from '../utils/articleDate';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

function Article() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: articles, isLoading, error } = useArticles();

  if (isLoading) {
    return (
      <div className="App">
        <Header />
        <div className="article-page">
          <div className="article-container">
            <LoadingSpinner />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="App">
        <Header />
        <div className="article-page">
          <div className="article-container">
            <p style={{ textAlign: 'center', padding: '2rem', color: 'red' }}>
              Error loading article.
            </p>
            <button onClick={() => navigate('/')} className="back-button">
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Find the article by id
  const allArticles = articles && articles.length > 0 ? articles : siteConfig.featuredStories;
  const article = allArticles.find((a) => String(a.id) === String(id));

  // Function to check if raw_html_input contains a URL and render accordingly
  const renderContent = () => {
    let content = article.raw_html_input || article.excerpt || '';
    
    console.log('Content:', content);
    console.log('Content type:', typeof content);
    
    // Check if content contains {URL} pattern anywhere in the text
    // More flexible pattern to handle any spacing and special characters
    const bracketPattern = /\{\s*([^}]+?)\s*\}/g;
    const matches = [...content.matchAll(bracketPattern)];
    
    console.log('Matches found:', matches.length);
    console.log('Matches:', matches);
    
    if (matches.length > 0) {
      // Process each URL found
      const elements = [];
      let lastIndex = 0;
      
      matches.forEach((match, index) => {
        const url = match[1].trim();
        const matchStart = match.index;
        
        console.log(`Processing match ${index}:`, url);
        
        // Add text before the URL
        if (matchStart > lastIndex) {
          const textBefore = content.substring(lastIndex, matchStart);
          elements.push(
            <div 
              key={`text-${index}`}
              className="article-body"
              dangerouslySetInnerHTML={{ __html: textBefore }}
            />
          );
        }
        
        // Check if this is a valid URL
        const isValidUrl = /^https?:\/\//i.test(url);
        
        if (!isValidUrl) {
          // Not a URL, treat as text
          elements.push(
            <div 
              key={`text-${index}`}
              className="article-body"
              dangerouslySetInnerHTML={{ __html: match[0] }}
            />
          );
          lastIndex = matchStart + match[0].length;
          return;
        }
        
        // Case 1: YouTube video URL - extract video ID from various YouTube URL formats
        let videoId = null;
        
        // Try to extract from watch?v= format (including with additional parameters)
        const watchMatch = url.match(/[?&]v=([^&]+)/);
        if (watchMatch) {
          videoId = watchMatch[1];
        }
        
        // Try to extract from youtu.be/ format
        if (!videoId) {
          const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
          if (shortMatch) {
            videoId = shortMatch[1];
          }
        }
        
        // Try to extract from embed/ format
        if (!videoId) {
          const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
          if (embedMatch) {
            videoId = embedMatch[1];
          }
        }
        
        // If we found a YouTube video ID, render the iframe
        if (videoId && (url.includes('youtube.com') || url.includes('youtu.be'))) {
          console.log('Rendering YouTube video:', videoId);
          elements.push(
            <div key={`video-${index}`} className="video-container" style={{ position: 'relative', paddingBottom: '56.25%', height: 0, overflow: 'hidden', marginTop: '2rem', marginBottom: '2rem' }}>
              <iframe
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                src={`https://www.youtube.com/embed/${videoId}`}
                title="YouTube video"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          );
        }
        // Case 2: Image URL (check for image extensions anywhere in the URL path)
        else {
          // Check if URL contains image extension in the path (before or including query params)
          const imagePattern = /\.(jpg|jpeg|png|gif|webp|svg|bmp)([?&#]|$)/i;
          const isImage = imagePattern.test(url);
          
          console.log('Is image?', isImage, 'URL:', url);
          
          if (isImage) {
            console.log('Rendering image:', url);
            elements.push(
              <div key={`image-${index}`} style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                <img 
                  src={url}
                  alt="Article content"
                  style={{ width: '100%', height: 'auto', borderRadius: '8px' }}
                  onError={(e) => {
                    console.error('Image failed to load:', url);
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            );
          } else {
            // If URL but not video or image, show as link
            console.log('Rendering as link:', url);
            elements.push(
              <div key={`link-${index}`} style={{ marginTop: '2rem', marginBottom: '2rem' }}>
                <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--cornell-red)', fontSize: '1.1rem' }}>
                  View Content: {url}
                </a>
              </div>
            );
          }
        }
        
        lastIndex = matchStart + match[0].length;
      });
      
      // Add remaining text after last URL
      if (lastIndex < content.length) {
        const textAfter = content.substring(lastIndex);
        elements.push(
          <div 
            key="text-end"
            className="article-body"
            dangerouslySetInnerHTML={{ __html: textAfter }}
          />
        );
      }
      
      return <>{elements}</>;
    }
    
    // Default: render as HTML
    return (
      <div 
        className="article-body"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  };

  if (!article) {
    return (
      <div className="App">
        <Header />
        <div className="article-page">
          <div className="article-container">
            <h1>Article Not Found</h1>
            <p>The article you're looking for doesn't exist.</p>
            <button onClick={() => navigate('/')} className="back-button">
              ← Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="App">
      <Header />
      <div className="article-page">
        <div className="article-container">
          {/* <button onClick={() => navigate('/')} className="back-button">
            ← Back to Home
          </button> */}
          
          <article className="article-content">
            <header className="article-header">
              <h1 className="article-title">{article.title}</h1>
              <p className="article-date">{formatArticleDate(article)}</p>
              <div className="article-categories">
                {article.category && article.category.split(',').map((cat, index) => (
                  <span key={index} className="article-category">{cat.trim()}</span>
                ))}
              </div>
            </header>
          
            {article.asset && (
              <img 
                src={article.asset} 
                alt={article.title} 
                className="article-featured-image"
              />
            )}
          
            {/* Render content based on type (URL video, URL image, or HTML) */}
            {renderContent()}
            <ArticleActions key={article.id} article={article} />
          </article>
        </div>
      </div>
    </div>
  );
}

export default Article;
